import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {DxPieChartModule} from "devextreme-angular";
import {ScoreDto} from "@services";
import {PercentPipe} from "@angular/common";
import {DxChartTypes} from "devextreme-angular/ui/chart";
import {CapitalCasePipe} from "../../../../../pipes/capital-case.pipe";
import { ChartDataPoint } from 'src/app/components/library/doughnut-chart/doughnut-chart.component';
import { hasScoreData, NoData, noDataColor, noDataRing, scoreCenterLabel } from 'src/app/components/library/doughnut-chart/score-center';

const GOLDEN_ANGLE = 137.508;

@Component({
    selector: 'app-score-breakdown-chart',
    imports: [DxPieChartModule],
    providers: [CapitalCasePipe],
    templateUrl: './score-breakdown-chart.component.html'
})
export class ScoreBreakdownChartComponent implements OnChanges {
    @Input() title: string = '';
    @Input() score: ScoreDto | null | undefined;
    @Input() centerText: string;
    @Input() formatMode: 'percent' | 'decimal' = 'percent';
    @Input() useCapitalCaseFormatting: boolean = true;
    @Input() palette: Record<string, string> = {};
    @Input() colorMode: 'palette' | 'index' = 'palette';
    @Input() legendRowCount: number = 2;

    private capitalCase = inject(CapitalCasePipe);
    protected items: ChartDataPoint[] = [];
    protected chartItems: ChartDataPoint[] = [];
    protected hasData = false;
    protected chartPalette: Record<string, string> = {};
    private percentPipe = new PercentPipe('en-US');

    centerValue: number | null | undefined;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.score || changes.palette) {
            this.applyScore();
        }
    }

    private applyScore(): void {
        this.centerValue = this.score?.avgScorePercent ?? null;

        this.chartPalette = { ...this.palette };
        if (this.score?.pallet) {
            Object.entries(this.score.pallet).forEach(([key, colorClass]) => {
                const varName = `--bs-${String(colorClass).toLowerCase()}`;
                const colorValue = getComputedStyle(document.body).getPropertyValue(varName).trim();
                if (colorValue) {
                    this.chartPalette[key] = colorValue;
                }
            });
        }

        this.items = Object.entries(this.score?.totals ?? {}).map(([key, value]) => ({
            argument: key,
            value: value
        }));
        this.hasData = hasScoreData(this.items);
        this.chartItems = this.hasData || !this.score ? this.items : noDataRing();
    }

    get centerLabel(): string {
        return this.score ? scoreCenterLabel(this.hasData, this.centerValue, this.formatMode) : '';
    }

    customizeTooltip = ({value, percent, argument}: { value: number, percent: number, argument: string }) => ({
        text: `${this.legendLabel(argument)}: ${this.formatMode === 'percent' ? this.percentPipe.transform(percent) : value}`,
    });

    protected markerColor(argument: string): string {
        return this.chartPalette[argument] || 'rgba(100, 100, 100, 0.5)';
    }

    protected indexColor(argument: string): string {
        const index = this.items.findIndex(item => item.argument === argument);
        const hue = (index * GOLDEN_ANGLE) % 360;
        return `hsl(${hue}, 65%, 55%)`;
    }

    customizePoint: DxChartTypes.Properties['customizePoint'] = (pointInfo: { argument: string, value: number }) => {
        return {
            color: pointInfo.argument === NoData ? noDataColor : this.legendColor(pointInfo.argument)
        };
    }

    protected legendColor(argument: string): string {
        return this.colorMode === 'index' ? this.indexColor(argument) : this.markerColor(argument);
    }

    protected legendLabel(argument: string): string {
        return this.useCapitalCaseFormatting ? this.capitalCase.transform(argument) : argument;
    }

    private chart: any;
    protected hiddenArguments = new Set<string>();

    onChartInitialized(e: { component?: any }): void {
        this.chart = e.component;
    }

    private getPoints(argument: string): any[] {
        const series = this.chart?.getAllSeries?.()[0];
        return series?.getPointsByArg?.(argument) ?? [];
    }

    protected onLegendClick(argument: string): void {
        const points = this.getPoints(argument);
        const willHide = points.some(p => p.isVisible());
        points.forEach(p => (willHide ? p.hide() : p.show()));
        if (willHide) {
            this.hiddenArguments.add(argument);
        } else {
            this.hiddenArguments.delete(argument);
        }
    }

    protected onLegendHover(argument: string, hovered: boolean): void {
        this.getPoints(argument).forEach(p => (hovered ? p.hover() : p.clearHover()));
    }
}
